import type { FetcherAvailability } from './types';
import { authedFetch } from '../../lib/authedFetch';

// R-130-10; raised 5s → 12s: GDELT's doc API takes ~8s to first byte
// (verified live), so a 5s probe budget reported a healthy upstream as
// 'down · timeout' on every Admin Sentiment Sources refresh.
export const SENTIMENT_PROBE_TIMEOUT_MS = 12_000;

interface ProbeHttpOptions {
  url: string;
  init?: RequestInit;
  requiredEnvVarName?: string;
  hasRequiredEnvVar?: boolean;
  unconfiguredStatuses?: number[];
  /** Some providers return an HTTP 200 credential-error page. */
  validateResponse?: (response: Response) => Promise<Pick<FetcherAvailability, 'state' | 'reason'> | null>;
  /**
   * R-130-10 — when true, route the browser-side probe through the
   * Firebase-gated `/api/proxy` (same egress the data path uses) instead of
   * hitting the upstream directly. Required for upstreams that do NOT send
   * CORS headers (e.g. GDELT), where a direct browser `fetch` is blocked and
   * surfaces as a misleading "Failed to fetch" TypeError rather than a real
   * health signal. Hosts that send `Access-Control-Allow-Origin` (FCC,
   * Socrata, ArcGIS Hub) leave this off and probe directly.
   */
  proxied?: boolean;
}

function finalizeAvailability(
  state: FetcherAvailability['state'],
  reason?: string,
  latencyMs?: number,
): FetcherAvailability {
  return {
    state,
    reason,
    latencyMs,
    probedAt: Date.now(), // R-130-10
  };
}

export function missingEnvAvailability(envVarName: string): FetcherAvailability {
  return finalizeAvailability('unconfigured', `missing ${envVarName}`); // R-130-10
}

export async function probeHttpAvailability(options: ProbeHttpOptions): Promise<FetcherAvailability> {
  const { url, init, requiredEnvVarName, hasRequiredEnvVar, unconfiguredStatuses = [401, 403], proxied = false } = options;
  if (requiredEnvVarName && !hasRequiredEnvVar) {
    return missingEnvAvailability(requiredEnvVarName); // R-130-10
  }

  // R-130-10 — in the browser, proxied probes go through the same Firebase-gated
  // `/api/proxy` the data path uses (avoids CORS on no-CORS upstreams like GDELT,
  // and carries the admin's bearer token). In Node / tests `window` is undefined,
  // so we keep the direct-`fetch` path the suite stubs against.
  const useProxy = proxied && typeof window !== 'undefined';
  const targetUrl = useProxy ? `/api/proxy?url=${encodeURIComponent(url)}` : url;
  const doFetch = useProxy ? authedFetch : fetch;

  const controller = new AbortController(); // R-130-10
  const timer = setTimeout(() => controller.abort(), SENTIMENT_PROBE_TIMEOUT_MS); // R-130-10
  const startedAt = Date.now();
  try {
    const res = await doFetch(targetUrl, { ...init, signal: controller.signal });
    const latencyMs = Date.now() - startedAt;
    if (res.ok) {
      const validation = await options.validateResponse?.(res);
      if (validation) return finalizeAvailability(validation.state, validation.reason, latencyMs);
      return finalizeAvailability('ok', undefined, latencyMs); // R-130-10
    }
    if (unconfiguredStatuses.includes(res.status)) {
      return finalizeAvailability('unconfigured', `HTTP ${res.status}`, latencyMs); // R-130-10
    }
    // HTTP 429 = upstream is UP but throttling us — that's a quota signal,
    // not an outage. Reporting it as 'down' caused false-negative health
    // chips for rate-limited-but-healthy sources.
    if (res.status === 429) {
      return finalizeAvailability('rate-limited', 'HTTP 429', latencyMs);
    }
    return finalizeAvailability('down', `HTTP ${res.status}`, latencyMs); // R-130-10
  } catch (error) {
    const err = error as { name?: string; message?: string };
    if (err?.name === 'AbortError') {
      return finalizeAvailability('down', 'timeout'); // R-130-10
    }
    return finalizeAvailability('down', err?.message || 'probe failed'); // R-130-10
  } finally {
    clearTimeout(timer);
  }
}
